import { motion } from "framer-motion";
import { Link } from "wouter";
import { QuickDiscovery } from "@/components/sections/QuickDiscovery";
import { Code2, Rocket, Zap, Smartphone, Paintbrush, Wrench, Layers, Workflow, Building2, ArrowRight, CheckCircle2, Star } from "lucide-react";
import { useListProjects, useListPosts, useListTestimonials, useListServices } from "@workspace/api-client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";
import { ProjectCard } from "@/components/common/ProjectCard";

function HomePostCard({ post, index }: { post: any; index: number }) {
  const { data: media } = useQuery<any[]>({
    queryKey: ["media", "POST", post.id],
    queryFn: async () => {
      const res = await fetch(`/api/media/POST/${post.id}`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!post.id,
  });

  const coverUrl =
    post.coverImage ||
    media?.find((m: any) => m.isFeatured)?.url ||
    media?.[0]?.url;

  return (
    <Link href={`/blog/${post.slug}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="group cursor-pointer"
      >
        <div className="aspect-[16/9] w-full rounded-xl bg-muted/30 mb-6 overflow-hidden relative">
          {coverUrl ? (
            <img src={coverUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20 group-hover:scale-105 transition-transform duration-500"></div>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
          <span>{post.createdAt ? format(new Date(post.createdAt), 'MMM d, yyyy') : 'Recent'}</span>
          <span>·</span>
          <span>{post.readTime || 5} min read</span>
        </div>
        <h3 className="font-display text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">{post.title}</h3>
        <p className="text-muted-foreground text-sm line-clamp-2">{post.excerpt}</p>
      </motion.div>
    </Link>
  );
}

export default function Home() {
  const { data: projectsData, isLoading: loadingProjects } = useListProjects({ featured: true });
  const { data: postsData, isLoading: loadingPosts } = useListPosts({ limit: 3 });
  const { data: testimonialsData, isLoading: loadingTestimonials } = useListTestimonials();

    const { data: servicesData, isLoading: loadingServices } = useListServices();

  const projects = projectsData && Array.isArray(projectsData) && projectsData.length > 0
    ? projectsData.slice(0, 3)
    : [];

  const posts = postsData && postsData.posts && postsData.posts.length > 0
    ? postsData.posts
    : [];

  const testimonials = testimonialsData && Array.isArray(testimonialsData) && testimonialsData.length > 0
    ? testimonialsData
    : [];

  const services = servicesData && Array.isArray(servicesData) && servicesData.length > 0
    ? servicesData.slice(0, 3)
    : [];

  const serviceIconMap: Record<string, any> = {
    'website-development': Code2,
    'mobile-app-development': Smartphone,
    'mvp-launch': Rocket,
    'ui-ux-redesign': Paintbrush,
    'speed-optimisation': Zap,
    'maintenance-support': Wrench,
    'feature-scaling': Layers,
    'automation-tools': Workflow,
    'technical-consulting': Building2
  };

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title="AccelerateByte | Elite Tech Studio"
        description="We are the premium technical partner for companies that need surgical speed, total precision, and scalable results. Elite engineering for high-growth companies."
      />
      <JsonLd schema={[
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is AccelerateByte?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AccelerateByte is an elite tech studio and premium software development agency based in Mumbai, India. We build custom websites, mobile apps, and MVPs for high-growth companies in the UK, US, UAE, and India."
              }
            },
            {
              "@type": "Question",
              "name": "What services does AccelerateByte offer?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AccelerateByte offers website development (React/Next.js), mobile app development (React Native), MVP launch, UI/UX redesign, speed optimisation, maintenance and support, feature scaling, automation tools, and technical consulting."
              }
            },
            {
              "@type": "Question",
              "name": "How fast does AccelerateByte deliver projects?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Most projects are delivered in 2-8 weeks. Simple prototypes and proof-of-concepts can be ready in as little as 48 hours. Our process is built to eliminate bureaucracy and ship fast without compromising quality."
              }
            },
            {
              "@type": "Question",
              "name": "What technology stack does AccelerateByte use?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We primarily use React, Next.js, Node.js, React Native, and TypeScript with modern cloud infrastructure on AWS, GCP, and Vercel. We select the best tools for each project's specific needs."
              }
            },
            {
              "@type": "Question",
              "name": "Where is AccelerateByte located?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AccelerateByte is headquartered in Mumbai, Maharashtra, India. We serve clients globally, with primary markets in the UK, US, UAE, and India."
              }
            },
            {
              "@type": "Question",
              "name": "Does AccelerateByte work with startups?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. AccelerateByte specialises in working with early-stage startups to launch investor-ready MVPs quickly. We understand startup timelines and help founders move fast without burning budget."
              }
            },
            {
              "@type": "Question",
              "name": "How can I contact AccelerateByte?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can email us at hello@acceleratebyte.com or book a technical scoping call at https://acceleratebyte.com/contact"
              }
            },
            {
              "@type": "Question",
              "name": "What makes AccelerateByte different from other software agencies?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Senior engineers only, no middle managers, direct communication, and a rapid delivery process built for speed. We act as an extension of your team and commit to long-term partnerships beyond launch."
              }
            }
          ]
        },
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How AccelerateByte Delivers Your Software Project",
          "description": "AccelerateByte's rapid 4-step delivery process for building and launching software products at maximum speed without compromising quality.",
          "step": [
            {
              "@type": "HowToStep",
              "position": 1,
              "name": "Discovery & Scoping",
              "text": "Define exact requirements, architecture, and timeline in days, not weeks. No endless meetings — just clear, actionable plans."
            },
            {
              "@type": "HowToStep",
              "position": 2,
              "name": "Design & Prototype",
              "text": "High-fidelity wireframes and interactive prototypes deliver immediate feedback before a single line of code is written."
            },
            {
              "@type": "HowToStep",
              "position": 3,
              "name": "Build & Test",
              "text": "Iterative weekly sprints with full transparency, rigorous QA, and continuous integration to ship reliable code fast."
            },
            {
              "@type": "HowToStep",
              "position": 4,
              "name": "Launch & Support",
              "text": "Zero-downtime deployment followed by proactive monitoring and dedicated scaling support as your product grows."
            }
          ]
        }
      ]} />
      <JsonLd schema={{
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": "https://acceleratebyte.com/#localbusiness",
        "name": "AccelerateByte",
        "url": "https://acceleratebyte.com",
        "image": "https://acceleratebyte.com/opengraph.jpg",
        "description": "Elite tech studio based in Mumbai. We build websites, mobile apps, MVPs, and scalable systems for high-growth companies in the UK, US, EU, and UAE.",
        "priceRange": "$$",
        "email": "hello@acceleratebyte.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Mumbai",
          "addressRegion": "Maharashtra",
          "addressCountry": "IN"
        },
        "areaServed": [
          { "@type": "Country", "name": "India" },
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Country", "name": "United States" },
          { "@type": "Country", "name": "United Arab Emirates" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Software Development Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Website Development" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mobile App Development" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "MVP Launch" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Technical Consulting" } }
          ]
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5",
          "reviewCount": "30",
          "bestRating": "5"
        }
      }} />
      {/* 1. HERO */}
      <section className="relative min-h-[90vh] flex flex-col items-center justify-center pt-32 pb-32 px-4 overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
        <div className="absolute inset-0 z-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at center, #ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="container relative z-10 mx-auto text-center flex flex-col items-center">
          <div className="animate-hero-fade-in inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-8" style={{ animationDelay: '0s' }}>
            <Zap className="mr-2 h-4 w-4" />
            Elite Tech Studio
          </div>

          <h1 className="animate-hero-fade-in font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight max-w-5xl mb-6 leading-[1.1]" style={{ animationDelay: '0.1s' }}>
            We Don't Just Build —{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600">
              We Accelerate.
            </span>
          </h1>

          <p className="animate-hero-fade-in text-lg md:text-xl text-muted-foreground max-w-2xl mb-10" style={{ animationDelay: '0.2s' }}>
            Code at the speed of business. We are the premium technical partner for companies that need surgical speed, total precision, and scalable results.
          </p>

          <div className="animate-hero-fade-in flex flex-col sm:flex-row gap-4 mb-16" style={{ animationDelay: '0.3s' }}>
            <Link href="/contact" className="inline-flex h-12 items-center justify-center rounded-md bg-gradient-to-r from-blue-700 to-purple-700 px-8 text-base font-medium text-white shadow-lg shadow-primary/20 transition-all hover:opacity-90 hover:scale-105">
              Start Your Project
            </Link>
            <Link href="/work" className="inline-flex h-12 items-center justify-center rounded-md border border-input bg-background/50 backdrop-blur-sm px-8 text-base font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground">
              See Our Work
            </Link>
          </div>

          <div className="animate-hero-fade-in grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 pt-8 border-t border-border/50 w-full max-w-4xl text-sm font-medium text-muted-foreground" style={{ animationDelay: '0.4s' }}>
            <div className="flex flex-col items-center justify-center gap-1"><span className="font-display text-2xl text-foreground font-bold">50+</span><span>Projects Delivered</span></div>
            <div className="flex flex-col items-center justify-center gap-1"><span className="font-display text-2xl text-foreground font-bold">UK & US</span><span>Clients Worldwide</span></div>
            <div className="flex flex-col items-center justify-center gap-1"><span className="font-display text-2xl text-foreground font-bold">48hr</span><span>Turnaround</span></div>
            <div className="flex flex-col items-center justify-center gap-1"><span className="font-display text-2xl text-foreground font-bold text-yellow-500 flex items-center">5<span className="text-lg ml-1">★</span></span><span>Average Rating</span></div>
          </div>
        </div>
      </section>

      {/* 2. TICKER/MARQUEE */}
      <div className="w-full bg-blue-700 border-y border-white/10 py-3 overflow-hidden flex whitespace-nowrap">
        <div className="animate-marquee flex gap-8 font-display font-bold text-sm tracking-widest text-primary-foreground uppercase">
          {[1, 2].map((set) => (
            <div key={set} className="flex gap-8">
              {Array(5).fill("SPEED · RELIABILITY · INNOVATION · SCALABILITY · RESULTS · BUILD FAST · SCALE SMART · CODE AT THE SPEED OF BUSINESS · ").map((text, i) => (
                <span key={i}>{text}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* 3. SERVICES PREVIEW */}
      {services.length > 0 && (
        <section className="py-24 px-4 container mx-auto">
          <div className="mb-16 text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Everything You Need to Move Faster</h2>
            <p className="text-muted-foreground">From MVP launches to legacy system modernization, we provide the elite engineering power to accelerate your vision.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((srv: any, i: number) => {
              const Icon = serviceIconMap[srv.slug] || Code2;
              return (
                <Link key={srv.slug || i} href={`/services/${srv.slug}`}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ delay: i * 0.1 }} className="group h-full rounded-2xl border border-border/50 bg-card p-8 transition-all hover:border-primary/50 hover:bg-card/80 cursor-pointer relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-6 opacity-0 translate-x-4 -translate-y-4 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all text-primary"><ArrowRight className="h-6 w-6" /></div>
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all"><Icon className="h-7 w-7" /></div>
                    <h3 className="font-display text-xl font-bold mb-3">{srv.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{srv.description}</p>
                  </motion.div>
                </Link>
              );
            })}
          </div>

        <div className="mt-12 text-center">
          <Link href="/services" className="inline-flex h-12 items-center justify-center rounded-md border border-input bg-background px-8 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors">
            View All {servicesData && servicesData.length > 0 ? servicesData.length : ''} Services
          </Link>
        </div>
      </section>
      )}

      {/* 4. HOW WE WORK */}
      <section className="py-24 px-4 bg-muted/20 border-y border-border/50">
        <div className="container mx-auto">
          <div className="mb-20 max-w-2xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Our Rapid Delivery Process</h2>
            <p className="text-muted-foreground text-lg">We eliminated the bureaucracy to focus entirely on shipping high-quality code. This is how we move fast without breaking things.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12 relative">
            <div className="hidden md:block absolute top-6 left-12 right-12 h-[1px] bg-border z-0"><div className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-primary to-transparent"></div></div>
            {[
              { step: "01", title: "Discovery & Scoping", desc: "Define exact requirements, architecture, and timeline in days, not weeks. No endless meetings." },
              { step: "02", title: "Design & Prototype", desc: "High-fidelity wireframes and interactive prototypes for immediate feedback before coding begins." },
              { step: "03", title: "Build & Test", desc: "Iterative weekly sprints with full transparency, rigorous QA, and continuous integration." },
              { step: "04", title: "Launch & Support", desc: "Seamless zero-downtime deployment followed by proactive monitoring and dedicated scaling support." }
            ].map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ delay: i * 0.15 }} className="relative z-10 pt-0 md:pt-4 flex flex-row md:flex-col gap-6 md:gap-0">
                <div className="shrink-0 h-12 w-12 rounded-full bg-background border-2 border-primary flex items-center justify-center font-display font-bold text-primary md:mb-8 shadow-[0_0_15px_rgba(59,130,246,0.3)]">{step.step}</div>
                <div><h3 className="font-display text-xl font-bold mb-3">{step.title}</h3><p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <QuickDiscovery />

      {/* 5. STATS */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { value: "50+", label: "Projects Delivered" },
              { value: "30+", label: "Happy Clients" },
              { value: "5", label: "Countries Served" },
              { value: "3x", label: "Faster Delivery" }
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="rounded-2xl bg-card border border-border/50 p-6 md:p-8 text-center flex flex-col items-center justify-center gap-2">
                <div className="font-display text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-white to-white/60">{stat.value}</div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FEATURED WORK */}
      {projects.length > 0 && (
        <section className="py-24 px-4 bg-muted/10 border-y border-border/50">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div className="max-w-2xl">
                <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Work That Speaks for Itself</h2>
                <p className="text-muted-foreground">We partner with ambitious companies to build products that dominate their markets.</p>
              </div>
              <Link href="/work" className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80">View Portfolio <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {projects.map((project: any, i: number) => (
                <ProjectCard key={project.slug || i} project={project} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7. WHY ACCELERATEBYTE */}
      <section className="py-24 px-4 container mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className="w-full lg:w-1/2">
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-6">Why Premium Brands Choose Us</h2>
            <p className="text-lg text-muted-foreground mb-8">We are not a typical agency. We operate as an elite strike team that integrates deeply with your vision to deliver unmatched technical excellence.</p>
            <Link href="/about" className="inline-flex h-12 items-center justify-center rounded-md border border-input bg-background px-8 text-base font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground">
              Learn Our Story
            </Link>
          </div>
          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { title: "Surgical Speed", desc: "No red tape. Just rapid execution and delivery." },
              { title: "Senior Talent Only", desc: "You work directly with proven senior engineers." },
              { title: "Modern Tech Stack", desc: "React, Next.js, and scalable cloud architectures." },
              { title: "Transparent Process", desc: "Clear communication, daily updates, zero surprises." },
              { title: "Scalable Results", desc: "Code built to handle millions of users from day one." },
              { title: "Long-Term Partners", desc: "We support your growth long after launch." }
            ].map((feature, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex gap-4 items-start">
                <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display font-bold mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 px-4 bg-muted/20 border-y border-border/50 overflow-hidden">
          <div className="container mx-auto text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-muted-foreground">Don't just take our word for it.</p>
          </div>

          {/* Infinite Carousel */}
          <div className="relative w-full flex overflow-x-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10"></div>

            <motion.div animate={{ x: ["0%", "-50%"] }} transition={{ ease: "linear", duration: 40, repeat: Infinity }} className="flex w-max gap-6 px-6">
              {[...testimonials, ...testimonials].map((testimonial: any, i: number) => (
                <div key={i} className="w-[350px] md:w-[450px] rounded-2xl bg-card border border-border/50 p-8 flex flex-col">
                  <div className="flex text-yellow-500 mb-6">
                    {Array.from({ length: testimonial.rating || 5 }).map((_, j) => <Star key={j} className="h-5 w-5 fill-current" />)}
                  </div>
                  <p className="text-lg mb-8 flex-1 italic text-muted-foreground">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary">{testimonial.name.charAt(0)}</div>
                    <div>
                      <div className="font-bold">{testimonial.name}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* 9. BLOG PREVIEW */}
      {posts.length > 0 && (
        <section className="py-24 px-4 container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Insights & Ideas</h2>
              <p className="text-muted-foreground">Technical deep-dives, industry trends, and engineering guides.</p>
            </div>
            <Link href="/blog" className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80">Read All Articles <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((post: any, i: number) => (
              <HomePostCard key={post.slug || i} post={post} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* 10. CTA BANNER */}
      <section className="py-32 px-4 relative overflow-hidden mb-12 border-y border-primary/20">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-background to-purple-900/40"></div>
        <div className="absolute inset-0 z-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: 'radial-gradient(circle at center, #3B82F6 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>

        <div className="container relative z-10 mx-auto text-center max-w-3xl">
          <h2 className="font-display text-4xl md:text-6xl font-bold mb-6 text-white tracking-tight">Ready to Accelerate Your Business?</h2>
          <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto">Stop waiting months for what we can build in weeks. Let's discuss your next big move and how we can get you there faster.</p>
          <Link href="/contact" className="inline-flex h-14 items-center justify-center rounded-md bg-white text-blue-950 px-8 text-lg font-bold shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all hover:scale-105 hover:bg-gray-100">
            Book a Technical Scoping Call
          </Link>
        </div>
      </section>
    </div>
  );
}
